import PredefinedPeriod from 'src/models/rental/workflow/PredefinedPeriod'
import PriceConfiguration from 'src/models/pricing/PriceConfiguration'
import { PeriodType } from 'src/models/period/enums/PeriodType'
import PeriodConfigurationJson from './interfaces/PeriodConfigurationJson'
import PeriodSelector from './PeriodSelector'
import CodelocksPeriodSelector from './CodelocksPeriodSelector'
import ScheduledPeriod from 'src/models/rental/workflow/ScheduledPeriod'
import PeriodTranslation from 'src/models/period/interfaces/PeriodTranslation'
import StaticTranslatable from 'src/models/period/Translation/StaticTranslatable'
import DynamicTranslatable from 'src/models/period/Translation/DynamicTranslatable'
import { DateTime } from 'luxon'
import DynamicTranslation from 'src/models/period/Translation/DynamicTranslation'

export default class PeriodConfiguration {
  periodType: PeriodType
  periodSelector: PeriodSelector | null
  codelocksPeriodSelector: CodelocksPeriodSelector | null
  predefinedPeriods: Array<PredefinedPeriod>
  scheduledPeriods: Array<ScheduledPeriod>
  skipDurationIfOnlyOnePeriod: boolean
  priceConfiguration: PriceConfiguration

  constructor(
    periodType: PeriodType,
    periodSelector: PeriodSelector | null,
    codelocksPeriodSelector: CodelocksPeriodSelector | null,
    predefinedPeriods: Array<PredefinedPeriod>,
    scheduledPeriods: Array<ScheduledPeriod>,
    priceConfiguration: PriceConfiguration,
    skipDurationIfOnlyOnePeriod: boolean,
  ) {
    this.periodType = periodType
    this.periodSelector = periodSelector
    this.codelocksPeriodSelector = codelocksPeriodSelector
    this.predefinedPeriods = predefinedPeriods
    this.scheduledPeriods = scheduledPeriods
    this.skipDurationIfOnlyOnePeriod = skipDurationIfOnlyOnePeriod
    this.priceConfiguration = priceConfiguration
  }

  hasOnlyOneDurationChoice(now: DateTime) {
    return this.getPeriodTranslations(now).length === 1
  }

  getPeriodTranslations(now: DateTime): Array<PeriodTranslation> {
    if (this.periodType === PeriodType.PREDEFINED) {
      const periods: Array<StaticTranslatable> = []
      this.predefinedPeriods.forEach((period) => {
        for (let count = 1; count < period.multiplier + 1; count++) {
          periods.push(
            new StaticTranslatable(
              `${period.key}.${count}`,
              `rental.predefined_selector.duration.${period.key}`,
              { count: count },
            ),
          )
        }
      })
      return periods
    } else if (this.periodType === PeriodType.PERIOD_SELECTOR) {
      return [
        new StaticTranslatable('week', 'reservation.pricing_table.week_price'),
        new StaticTranslatable('day', 'reservation.pricing_table.day_price'),
        new StaticTranslatable('hour', 'reservation.pricing_table.hour_price'),
      ]
    } else if (this.periodType === PeriodType.CODELOCKS_PERIOD_SELECTOR) {
      return [
        new StaticTranslatable('day', 'reservation.pricing_table.day_price'),
        new StaticTranslatable('hour', 'reservation.pricing_table.hour_price'),
      ]
    } else if (this.periodType === PeriodType.SCHEDULED) {
      return this.scheduledPeriods
        .filter((period) => period.isActive(now))
        .map(
          (period) =>
            new DynamicTranslatable(
              period.reference,
              Object.fromEntries(
                Object.entries(period.translations).map(([locale, t]) => [
                  locale,
                  new DynamicTranslation(t.locale, t.label),
                ]),
              ),
            ),
        )
    }

    throw new Error('Not implemented period type')
  }

  static fromJson(data: PeriodConfigurationJson) {
    return new this(
      data.periodType,
      data.periodSelector ? PeriodSelector.fromJson(data.periodSelector) : null,
      data.codelocksPeriodSelector
        ? CodelocksPeriodSelector.fromJson(data.codelocksPeriodSelector)
        : null,
      data.predefinedPeriods.map((item) => PredefinedPeriod.fromJson(item)),
      data.scheduledPeriods.map((item) => ScheduledPeriod.fromJson(item)),
      PriceConfiguration.fromJson(data.priceConfiguration),
      data.skipDurationIfOnlyOnePeriod,
    )
  }
}
